import { v4 as uuidv4 } from 'uuid'

/**
 * Regenerates all condition and group IDs in a pattern and remaps prerequisite references.
 * This is used when duplicating or consuming shared patterns to ensure IDs are unique.
 *
 * @param pattern - The pattern object to modify (modified in place)
 * @returns The same pattern object with regenerated IDs
 */
export function regeneratePatternIds(pattern: any) {
  // Build a map of old IDs to new IDs before regenerating
  const idMap = new Map<string, string>()

  const collectOldIds = (group: any) => {
    const newId = uuidv4()
    idMap.set(group.id, newId)
    for (const condition of group.conditions || []) {
      const newConditionId = uuidv4()
      idMap.set(condition.id, newConditionId)
    }
    for (const nestedGroup of group.conditionGroups || []) {
      collectOldIds(nestedGroup)
    }
  }

  // Collect IDs from both buy and sell groups
  for (const group of pattern.buyConditionGroups || []) {
    collectOldIds(group)
  }
  for (const group of pattern.sellConditionGroups || []) {
    collectOldIds(group)
  }

  // Update all IDs and prerequisite references
  const updateIdsAndPrereqs = (group: any) => {
    group.id = idMap.get(group.id)!

    for (const condition of group.conditions || []) {
      condition.id = idMap.get(condition.id)!
      condition.sourceGroupId = group.id

      // Remap prerequisite references
      if (condition.settings?.prerequisites) {
        const updatedPrereqs = condition.settings.prerequisites
          .map((prereq: any) => {
            const newId = idMap.get(prereq.id)
            // Only keep prerequisite if it exists in the ID map (within pattern)
            if (newId) {
              return { ...prereq, id: newId }
            }
            return null
          })
          .filter(
            (prereq: any): prereq is { type: 'condition' | 'group'; id: string } => prereq !== null
          )

        if (updatedPrereqs.length > 0) {
          condition.settings.prerequisites = updatedPrereqs
        } else {
          delete condition.settings.prerequisites
        }
      }
    }

    for (const nestedGroup of group.conditionGroups || []) {
      nestedGroup.sourceGroupId = group.id
      updateIdsAndPrereqs(nestedGroup)
    }
  }

  // Update both buy and sell groups
  for (const group of pattern.buyConditionGroups || []) {
    updateIdsAndPrereqs(group)
  }
  for (const group of pattern.sellConditionGroups || []) {
    updateIdsAndPrereqs(group)
  }

  return pattern
}
